import type { SnakeTrailPoint } from "../../../lib/multiplayer/snake-trail";
import {
  appendSnakeTrailNorm,
  MP_SNAKE_TRAIL_START_MS,
  mpSnakeEdgeInsetPx,
  mpSnakeLayoutScale,
} from "../../../lib/multiplayer/snake-trail";
import { mpNormToCanvas, MP_REF_H, MP_REF_W } from "../../../lib/multiplayer/normal-sim";
import { mpClient } from "./client";

export type MpSnakeTrailDelta = { id: string; points: SnakeTrailPoint[] };

const trails = new Map<string, SnakeTrailPoint[]>();
const trailColors = new Map<string, string>();

function trailHalfWidth(layoutScale: number): number {
  return Math.max(4 * layoutScale, 5);
}

export function resetMpSnakeTrails(): void {
  trails.clear();
  trailColors.clear();
}

/** Match server trail spawn delay (local prediction only). */
function mpSnakeTrailSpawnActive(): boolean {
  const startMs = mpClient.gameStartTime;
  if (startMs == null) return false;
  return Date.now() - startMs >= MP_SNAKE_TRAIL_START_MS;
}

function mergeTrailPoints(existing: SnakeTrailPoint[], incoming: SnakeTrailPoint[]): void {
  if (incoming.length === 0) return;
  for (const pt of incoming) {
    appendSnakeTrailNorm(existing, pt.x, pt.y, mpSnakeLayoutScale());
  }
}

/** Replace stored trails (all players) — keeps spectators in sync. */
export function applyMpSnakeTrailFull(full: MpSnakeTrailDelta[] | undefined): void {
  if (!full?.length) return;
  for (const entry of full) {
    if (!entry?.id || !Array.isArray(entry.points)) continue;
    if (entry.points.length === 0) {
      trails.delete(entry.id);
      continue;
    }
    trails.set(entry.id, [...entry.points]);
  }
}

export function applyMpSnakeTrailDeltas(deltas: MpSnakeTrailDelta[] | undefined): void {
  if (!deltas?.length) return;
  for (const d of deltas) {
    if (!d?.id || !Array.isArray(d.points) || d.points.length === 0) continue;
    const existing = trails.get(d.id) ?? [];
    mergeTrailPoints(existing, d.points);
    trails.set(d.id, existing);
  }
}

/** Client-side trail for the local player so their own path is visible immediately. */
export function tickLocalMpSnakeTrail(
  playerId: string | null,
  nx: number,
  ny: number,
  color: string,
): void {
  if (!playerId || !mpSnakeTrailSpawnActive()) return;
  trailColors.set(playerId, color);
  const existing = trails.get(playerId) ?? [];
  appendSnakeTrailNorm(existing, nx, ny, mpSnakeLayoutScale());
  trails.set(playerId, existing);
}

export function syncMpSnakeTrailColors(entries: Array<{ id: string; trailColor?: string }>): void {
  for (const e of entries) {
    if (e.trailColor) trailColors.set(e.id, e.trailColor);
  }
}

/** Draw synced play-area border for Snake MP (matches server collision inset). */
export function drawMpSnakePlayBorder(
  ctx: CanvasRenderingContext2D,
  cw: number,
  ch: number,
  color: string,
  layoutScale: number,
): void {
  if (cw <= 0 || ch <= 0) return;
  const insetNormX = mpSnakeEdgeInsetPx() / MP_REF_W;
  const insetNormY = mpSnakeEdgeInsetPx() / MP_REF_H;
  const tl = mpNormToCanvas(insetNormX, insetNormY, cw, ch);
  const br = mpNormToCanvas(1 - insetNormX, 1 - insetNormY, cw, ch);
  const width = br.x - tl.x;
  const height = br.y - tl.y;
  if (width <= 0 || height <= 0) return;
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(2, 2 * layoutScale);
  ctx.shadowColor = color;
  ctx.shadowBlur = 8 * layoutScale;
  ctx.globalAlpha = 0.95;
  ctx.strokeRect(tl.x, tl.y, width, height);
  ctx.restore();
}

/** Draw all stored snake trails — visible to every player and spectator. */
export function drawMpSnakeTrails(
  ctx: CanvasRenderingContext2D,
  cw: number,
  ch: number,
  layoutScale: number,
  fallbackColor: string,
): void {
  for (const [id, points] of trails) {
    if (points.length < 2) continue;
    const color = trailColors.get(id) ?? fallbackColor;
    const half = trailHalfWidth(layoutScale);
    ctx.save();
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = color;
    ctx.lineWidth = half * 2;
    ctx.shadowColor = color;
    ctx.shadowBlur = 10 * layoutScale;
    ctx.globalAlpha = 0.92;
    ctx.beginPath();
    const first = mpNormToCanvas(points[0]!.x, points[0]!.y, cw, ch);
    ctx.moveTo(first.x, first.y);
    for (let i = 1; i < points.length; i++) {
      const p = mpNormToCanvas(points[i]!.x, points[i]!.y, cw, ch);
      ctx.lineTo(p.x, p.y);
    }
    ctx.stroke();
    ctx.restore();
  }
}
